import { DateTime } from 'luxon'

import { getUserTimezone } from '@/insights/common/timezone'

export const standardDateTimeFormat = 'dd-MMM-yyyy hh:mm a'

export const formatSeconds = (seconds: number): string => {
  const totalMinutes = Math.floor(seconds / 60)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return `${hours}:${String(minutes).padStart(2, '0')}`
}

export const formatMinutes = (minutes: number | null | undefined): string => {
  if (minutes === null || minutes === undefined) return '0.00'
  return (Math.max(0, minutes) / 60).toFixed(2)
}

/** Format a duration in seconds as H:MM:SS, retaining hours beyond a day. */
export const formatDurationSeconds = (seconds: number): string => {
  const safeSeconds = Math.max(0, Math.floor(seconds))
  const hours = Math.floor(safeSeconds / 3600)
  const minutes = Math.floor((safeSeconds % 3600) / 60)
  const remainingSeconds = safeSeconds % 60
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`
}

export const formatDate = (value: string | Date, format: string = 'dd-MM-yyyy HH:mm', zone?: string) => {
  const date = value instanceof Date ? value : new Date(value)
  const dt = DateTime.fromJSDate(date)
  return (zone ? dt.setZone(zone) : dt).toFormat(format)
}

/** Format a trip timestamp in the user's profile timezone, without a leading zero on the hour. */
const tripDateTimeFormat = 'dd-MMM-yyyy h:mm a'

export const formatTripDate = (value: Date | string | null): string =>
  value ? formatDate(value, tripDateTimeFormat, getUserTimezone()) : ''

/**
 * Format a timestamp for history graph tooltips: '08-Mar-2026 6:31 PM'.
 * When a zone is given, the time renders in that zone and the timezone
 * abbreviation is appended (e.g. '08-Mar-2026 6:31 PM EDT').
 */
export function formatHistoryDateTime(value: string | Date | number, zone?: string): string {
  const base =
    typeof value === 'number'
      ? DateTime.fromMillis(value)
      : DateTime.fromJSDate(value instanceof Date ? value : new Date(value))
  const dt = zone ? base.setZone(zone) : base
  return dt.toFormat(zone ? 'dd-MMM-yyyy h:mm a ZZZZ' : 'dd-MMM-yyyy h:mm a')
}

/** Format duration between two epoch-ms timestamps: '2h 15m' or '45m' */
export function formatDuration(startMs: number, endMs: number): string {
  const diff = endMs - startMs
  const h = Math.floor(diff / 3600000)
  const m = Math.floor((diff % 3600000) / 60000)
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

/** Format duration with full words: '2 hours 15 minutes 37 seconds' */
export function formatDurationLong(startMs: number, endMs: number): string {
  const diff = endMs - startMs
  const h = Math.floor(diff / 3600000)
  const m = Math.floor((diff % 3600000) / 60000)
  const s = Math.floor((diff % 60000) / 1000)
  const parts: string[] = []
  if (h > 0) parts.push(`${h} hour${h !== 1 ? 's' : ''}`)
  if (m > 0) parts.push(`${m} minute${m !== 1 ? 's' : ''}`)
  if (s > 0 || parts.length === 0) parts.push(`${s} second${s !== 1 ? 's' : ''}`)
  return parts.join(' ')
}

/**
 * Format a timestamp in the user's profile timezone. Prefer this over formatDate for
 * user-facing values — omitting formatDate's `zone` argument silently renders in the
 * browser's zone, which drifts from the rest of the UI.
 */
export const formatUserDate = (value?: string | Date | null, fallback = '-'): string =>
  value ? formatDate(value, standardDateTimeFormat, getUserTimezone()) : fallback
